import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
// import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/Toaster";
import { AuthProvider } from "./contexts/AuthContext";
import { SocketProvider } from "./contexts/SocketContext";
import { NoticeProvider } from "./contexts/NoticeContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { useAuthStore } from "./store/authStore";
import { savePostAuthRedirect } from "./utils/postAuthRedirect";
import ErrorBoundary from "./components/ErrorBoundary";

// Layout Components
import MainLayout from "./layouts/MainLayout";
import AuthLayout from "./layouts/AuthLayout";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import GroupsPage from "./pages/GroupsPage";
import CreateGroupPage from "./pages/CreateGroupPage";
import GroupDetailPage from "./pages/GroupDetailPage";
import GroupManagePage from "./pages/GroupManagePage";
import GroupChatPage from "./pages/GroupChatPage";
import ProfilePage from "./pages/ProfilePage";
import EventsPage from "./pages/EventsPage";
import EventDetailsPage from "./pages/EventDetailsPage";
import CreateEventPage from "./pages/CreateEventPage";
import DashboardPage from "./pages/DashboardPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import EventManagementPage from "./pages/admin/EventManagementPage";
import EventLandingPageEditor from "./pages/admin/EventLandingPageEditor";
import CatchAllRouteHandler from "./components/CatchAllRouteHandler";
import UserManagementPage from "./pages/admin/UserManagementPage";
import GroupManagementPage from "./pages/admin/GroupManagementPage";
import PaymentManagementPage from "./pages/admin/PaymentManagementPage";
import AdminReportsPage from "./pages/AdminReportsPage";
import AccountUpgradesPage from "./pages/AccountUpgradesPage";
import MessagesPage from "./pages/MessagesPage";
import DonatePage from "./pages/DonatePage";
import DonationSuccessPage from "./pages/DonationSuccessPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentTestPage from "./pages/PaymentTestPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import HelpPage from "./pages/HelpPage";
import BlogListPage from "./pages/BlogListPage";
import BlogPostPage from "./pages/BlogPostPage";
import BlogManagementPage from "./pages/admin/BlogManagementPage";
import BlogEditorPage from "./pages/admin/BlogEditorPage";

// Testing Components
import RouteTest from "./components/RouteTest";
import NavHealthCheck from "./components/NavHealthCheck";
import ApiHealthCheck from "./components/ApiHealthCheck";
import ResponsiveTest from "./components/ResponsiveTest";


// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    // Preserve where the (logged-out) visitor was trying to go so the auth flow
    // can bring them back — critical for shared group-join links. localStorage
    // is authoritative because e-mail verification may happen in another tab or
    // on a different device; router state alone would be lost there.
    savePostAuthRedirect(location.pathname + location.search);
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // Check if user's email is verified
  if (user && !user.emailVerified) {
    // The visitor is logged in but unverified; remember their intended target
    // (e.g. a group link) so verification can complete the journey.
    savePostAuthRedirect(location.pathname + location.search);
    return <Navigate to="/verify-email" replace />;
  }

  return <>{children}</>;
};

// Public Route wrapper (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      {/* <HelmetProvider> */}
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <SocketProvider>
              <NotificationProvider>
                <NoticeProvider>
                <div className="min-h-screen">
          <Routes>
            
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/donate" element={<DonatePage />} />
            <Route path="/donate/success" element={<DonationSuccessPage />} />
            <Route path="/premium/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/privacy-statement" element={<PrivacyPage />} />
            <Route path="/algemene-voorwaarden" element={<TermsPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            

            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <LoginPage />
                  </AuthLayout>
                </PublicRoute>
              }
            />

            <Route
              path="/register"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <RegisterPage />
                  </AuthLayout>
                </PublicRoute>
              }
            />

            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <ForgotPasswordPage />
                  </AuthLayout>
                </PublicRoute>
              }
            />

            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <ResetPasswordPage />
                  </AuthLayout>
                </PublicRoute>
              }
            />

            <Route
              path="/verify-email"
              element={<VerifyEmailPage />}
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <DashboardPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/events"
              element={
                <ProtectedRoute>
                  <EventManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/events/:eventId/landing-page"
              element={
                <ProtectedRoute>
                  <EventLandingPageEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute>
                  <BlogManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/blog/new"
              element={
                <ProtectedRoute>
                  <BlogEditorPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/blog/:id"
              element={
                <ProtectedRoute>
                  <BlogEditorPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/groups"
              element={
                <ProtectedRoute>
                  <GroupManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute>
                  <PaymentManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute>
                  <AdminReportsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <EventsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/events/create"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CreateEventPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/events/:slug"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <EventDetailsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/groups"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <GroupsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/groups/create"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CreateGroupPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/groups/:groupId"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <GroupDetailPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/groups/:groupId/manage"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <GroupManagePage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/groups/:groupId/chat"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <GroupChatPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProfilePage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <MessagesPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/account-upgrades"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <AccountUpgradesPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/* Testing Routes (Development Only) */}
            <Route path="/test/routes" element={<RouteTest />} />
            <Route path="/test/nav-health" element={<NavHealthCheck />} />
            <Route path="/test/api-health" element={<ApiHealthCheck />} />
            <Route path="/test/responsive" element={<ResponsiveTest />} />
            <Route path="/test/payments" element={<PaymentTestPage />} />

            {/* Catch all route - handles landing pages and unknown routes */}
            <Route path="*" element={<CatchAllRouteHandler />} />
          </Routes>

          <Toaster />
                </div>
              </NoticeProvider>
            </NotificationProvider>
          </SocketProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
